import {handleActions} from 'redux-actions';
import {Record, List} from 'immutable';
import moment from 'moment-timezone';

import {leavePeriodFromApi} from 'web-app/react/profile/leave-widget/leave-from-api-adapter';
import {hasValue} from '@famly/stat_ts-utils_has-value';

import * as Actions from './actions';
import {createDefaultSelectedYear} from './helpers';

// this is only used for children leaves and not employee leaves
// that's why it doesn't have CHILD_SICK
const LeaveSummaryStat = Record({
    SICK: 0,
    VACATION: 0,
    ABSENT: 0,
});

export const Period = Record({
    from: '',
    to: '',
});

export const BradfordFactor = Record({
    score: 0,
    numberSickDays: 0,
    numberSickPeriods: 0,
});

const bradfordFactorFromApi = bradfordFactor => {
    return new BradfordFactor(bradfordFactor);
};

export const LeaveSummary = Record({
    leaves: List(), // List of LeavePeriods
    months: List(),
    past: new LeaveSummaryStat(),
    totals: new LeaveSummaryStat(),
    period: new Period(),
});

/**
 * this function is responsible for injecting the result from the core /summary API
 * into the the leave widget that has two flows
 * -----> the employee flow
 * -----> the child flow
 * @param  leaveSummaries
 * @returns
 */
const leaveSummariesFromApi = leaveSummaries => {
    return leaveSummaries.map(leave => {
        return {
            leaves: List(leave.leaves).map(leavePeriodFromApi),
            months: List(leave.months),
            // if the leave.past.hours are defined, that means we are dealing
            // past: {hours: leave.past.hours, days: leave.past.days} <------ which is a an employee specific payload
            // with an Employee summary payload
            // otherwise we use past: leave.past <---- which is a Child summary payload
            past: hasValue(leave.past.hours) ? {hours: leave.past.hours, days: leave.past.days} : leave.past,
            totals: hasValue(leave.past.totals) ? {hours: leave.totals.hours, days: leave.totals.days} : leave.totals,
            period: new Period(leave.period),
        };
    });
};

export const LeavePeriod = Record({
    startDate: null,
    endDate: null,
    duration: 0,
    leaveType: null,
    body: null,
    vacationId: null,
    deleting: false,
    employeeLeaveIds: null,
    leaveSubType: null,
    isPDL: null,
});

const vacationReplyFromApi = vacationReply => {
    const days = List(vacationReply.days);
    const first = days.first();
    const last = days.last();
    return new LeavePeriod({
        body: vacationReply.title,
        startDate: moment(first.day_localdate, 'YYYY-MM-DD'),
        endDate: days.size > 1 ? moment(last.day_localdate, 'YYYY-MM-DD') : null,
        duration: days.size > 1 ? moment(last.day_localdate, 'YYYY-MM-DD').diff(first.day, 'days') + 2 : null,
        vacationId: vacationReply.vacationId,
        leaveType: 'VACATION',
    });
};

const State = Record({
    loading: false,
    selectedYear: '',
    leaveSummaries: List(),
    vacationReplies: List(),
    personType: '',
    bradfordFactor: null,
});

export default handleActions(
    {
        [Actions.reset.type]: () => new State(),
        [Actions.fetch.type]: (state, {payload}) => {
            return state.merge({
                loading: true,
                personType: payload.personType,
            });
        },
        [Actions.fetchSuccess.type]: (state, {payload}) => {
            return state.merge({
                loading: false,
                leaveSummaries: leaveSummariesFromApi(payload.leave),
                vacationReplies: List(payload.vacation).map(vacation => vacationReplyFromApi(vacation)),
                selectedYear: createDefaultSelectedYear(payload.leave),
                bradfordFactor: payload.bradfordFactor ? bradfordFactorFromApi(payload.bradfordFactor) : null,
            });
        },
        [Actions.fetchFailed.type]: state =>
            state.merge({
                loading: false,
            }),
        [Actions.changeYear.type]: (state, {payload}) =>
            state.merge({
                selectedYear: payload.year,
            }),
    },
    new State(),
);
